// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
/**
 * EspertoBustaPagaQuiz — Deployment 13
 *
 * Embedded at the bottom of /guida-busta-paga.
 * 5 multiple-choice questions per spec. Awards the
 * "esperto-busta-paga" badge on 5/5 (uses POST /api/me/badges
 * with $addToSet to avoid duplicates).
 */
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Share2, RotateCcw, CheckCircle2, XCircle } from 'lucide-react';
import { useAuth } from '../App';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Exact spec questions: A, B, C, C, A (correct = 0, 1, 2, 2, 0)
const QUESTIONS = [
  {
    q: 'Cosa indica la sigla RAL?',
    opts: [
      'Reddito Annuo Lordo',
      'Reddito Agevolato Lavorativo',
      'Rata Annua Lavorativa',
      'Reddito Aziendale Liquidato',
    ],
    correct: 0,
  },
  {
    q: 'Quale percentuale di contributi INPS paga il lavoratore dipendente nel 2026?',
    opts: ['6,91%', '9,19%', '11,50%', '7,65%'],
    correct: 1,
  },
  {
    q: 'Cosa sono le detrazioni da lavoro dipendente?',
    opts: [
      'Importi che aumentano le tasse dovute',
      "Rimborsi erogati dall'INPS",
      "Riduzioni dell'IRPEF calcolate automaticamente dal datore di lavoro",
      'Bonus una tantum dello Stato',
    ],
    correct: 2,
  },
  {
    q: 'Il TFR viene erogato:',
    opts: [
      'Ogni mese insieme allo stipendio',
      'Solo in caso di licenziamento',
      'Alla fine del rapporto di lavoro per qualsiasi motivo',
      'Solo dopo 10 anni di lavoro',
    ],
    correct: 2,
  },
  {
    q: 'La tredicesima mensilità viene calcolata su:',
    opts: [
      'La retribuzione base mensile',
      'Stipendio base + tutti i bonus',
      'Il reddito lordo annuo diviso 13',
      'Il netto mensile moltiplicato per 1,5',
    ],
    correct: 0,
  },
];

const SHARE_TEXT = 'Ho ottenuto il badge Esperto di Busta Paga su SAPIENS 🏆\nMetti alla prova anche tu:\nhttps://www.finsapiens.it/guida-busta-paga';

export default function EspertoBustaPagaQuiz() {
  const { user, refreshUser } = useAuth();
  const [phase, setPhase] = useState('intro'); // intro | quiz | result
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]); // selected option indexes
  const [chosen, setChosen] = useState(null);
  const [feedback, setFeedback] = useState(false);
  const [toast, setToast] = useState('');
  const [shareUrl, setShareUrl] = useState('');
  const [badgeSaved, setBadgeSaved] = useState(false);

  const score = answers.filter((a, i) => a === QUESTIONS[i].correct).length;
  const isPerfect = score === 5;

  const start = () => { setPhase('quiz'); setIdx(0); setAnswers([]); setChosen(null); setFeedback(false); setBadgeSaved(false); };

  const submitAnswer = () => {
    if (chosen === null) return;
    setFeedback(true);
  };

  const next = async () => {
    const newAnswers = [...answers, chosen];
    setAnswers(newAnswers);
    setChosen(null);
    setFeedback(false);
    if (idx + 1 >= QUESTIONS.length) {
      // Quiz finished — compute score & save badge if 5/5 + logged-in
      const finalScore = newAnswers.filter((a, i) => a === QUESTIONS[i].correct).length;
      setPhase('result');
      if (finalScore === 5 && user && !badgeSaved) {
        try {
          await fetch(`${API_URL}/api/me/badges`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ badge_id: 'esperto-busta-paga' }),
          });
          setBadgeSaved(true);
          if (refreshUser) refreshUser();
        } catch {/* offline-safe */}
      }
    } else {
      setIdx(i => i + 1);
    }
  };

  const handleShare = async () => {
    const text = SHARE_TEXT;
    const url = 'https://www.finsapiens.it/guida-busta-paga';
    try {
      if (navigator.share) {
        await navigator.share({ title: 'Esperto di Busta Paga', text, url });
        return;
      }
    } catch {/* ignored */}
    try {
      await navigator.clipboard.writeText(text);
      setToast('Link copiato!');
      setTimeout(() => setToast(''), 2000);
      return;
    } catch {/* ignored */}
    setShareUrl(url);
  };

  // ─── INTRO ─────────────────────────────────────────────────
  if (phase === 'intro') {
    return (
      <div className="mt-8" data-testid="esperto-quiz-section">
        <div className="p-6 sm:p-8 rounded-2xl text-center" style={{ background: 'linear-gradient(135deg, rgba(201,168,76,0.08) 0%, rgba(61,122,82,0.06) 100%)', border: '1px solid rgba(201,168,76,0.25)' }}>
          <Trophy className="w-12 h-12 mx-auto mb-3 text-[#C9A84C]" />
          <h2 className="text-2xl font-bold text-[#E8E8E4] mb-2">Metti alla prova le tue conoscenze</h2>
          <p className="text-sm text-[#888780] mb-6">5 domande sulla busta paga</p>
          <button onClick={start} data-testid="esperto-quiz-start"
            className="sapiens-btn-primary px-6 py-3 rounded-xl font-semibold text-sm">
            Inizia il quiz →
          </button>
        </div>
      </div>
    );
  }

  // ─── QUIZ ──────────────────────────────────────────────────
  if (phase === 'quiz') {
    const q = QUESTIONS[idx];
    return (
      <div className="mt-8" data-testid="esperto-quiz-section">
        <div className="p-6 rounded-2xl" style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.08)' }}>
          <div className="flex items-center justify-between mb-4 text-xs text-[#888780]">
            <span>Domanda {idx + 1} di {QUESTIONS.length}</span>
            <span>Esperto di Busta Paga</span>
          </div>
          <div className="h-1 bg-[#1A1A18] rounded-full overflow-hidden mb-6">
            <div className="h-full bg-[#C9A84C] transition-all" style={{ width: `${((idx + 1) / QUESTIONS.length) * 100}%` }} />
          </div>
          <h3 className="text-lg font-semibold text-[#E8E8E4] mb-5" data-testid={`quiz-question-${idx}`}>{q.q}</h3>
          <div className="space-y-2.5 mb-6">
            {q.opts.map((opt, i) => {
              const isChosen = chosen === i;
              const showCorrect = feedback && i === q.correct;
              const showWrong = feedback && isChosen && i !== q.correct;
              return (
                <button
                  key={i}
                  onClick={() => !feedback && setChosen(i)}
                  disabled={feedback}
                  data-testid={`quiz-option-${idx}-${i}`}
                  className="w-full text-left px-4 py-3 rounded-xl transition-all text-sm flex items-center gap-3"
                  style={{
                    background: showCorrect ? 'rgba(61,122,82,0.15)' : showWrong ? 'rgba(180,60,60,0.15)' : isChosen ? 'rgba(201,168,76,0.12)' : '#1A1A18',
                    border: `1px solid ${showCorrect ? '#3D7A52' : showWrong ? '#B43C3C' : isChosen ? '#C9A84C' : 'rgba(255,255,255,0.06)'}`,
                    color: '#E8E8E4',
                    cursor: feedback ? 'default' : 'pointer',
                  }}
                >
                  <span style={{ width: 24, height: 24, borderRadius: '50%', background: isChosen || showCorrect || showWrong ? (showCorrect ? '#3D7A52' : showWrong ? '#B43C3C' : '#C9A84C') : 'transparent', border: '1px solid rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: '#1A1A18', flexShrink: 0 }}>
                    {String.fromCharCode(65 + i)}
                  </span>
                  <span className="flex-1">{opt}</span>
                  {showCorrect && <CheckCircle2 className="w-4 h-4 text-[#3D7A52]" />}
                  {showWrong && <XCircle className="w-4 h-4 text-[#B43C3C]" />}
                </button>
              );
            })}
          </div>
          {!feedback ? (
            <button onClick={submitAnswer} disabled={chosen === null} data-testid="quiz-submit"
              className="w-full sapiens-btn-primary px-6 py-3 rounded-xl font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed">
              Conferma risposta
            </button>
          ) : (
            <button onClick={next} data-testid="quiz-next"
              className="w-full sapiens-btn-primary px-6 py-3 rounded-xl font-semibold text-sm">
              {idx + 1 >= QUESTIONS.length ? 'Vedi risultato' : 'Prossima domanda →'}
            </button>
          )}
        </div>
      </div>
    );
  }

  // ─── RESULT ────────────────────────────────────────────────
  return (
    <div className="mt-8" data-testid="esperto-quiz-section">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        className="p-6 sm:p-8 rounded-2xl text-center"
        style={{ background: '#252523', border: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="text-5xl mb-3" data-testid="quiz-score">{score}/5</div>

        {isPerfect ? (
          <div data-testid="quiz-badge-section">
            <Trophy className="w-12 h-12 mx-auto text-[#C9A84C] mb-3" />
            <h3 className="text-xl font-bold text-[#E8E8E4] mb-2" data-testid="quiz-badge-title">🏆 Esperto di Busta Paga</h3>
            <p className="text-sm text-[#888780] mb-5" data-testid="quiz-badge-text">Hai dimostrato di conoscere perfettamente la tua busta paga.</p>
            {!user && (
              <p className="text-xs text-[#C9A84C] mb-4 italic" data-testid="quiz-register-hint">
                Registrati per salvare il tuo badge.
              </p>
            )}
            <div className="flex flex-wrap gap-3 justify-center">
              <button onClick={handleShare} data-testid="quiz-share-btn"
                className="px-5 py-2.5 rounded-xl font-semibold text-sm sapiens-btn-primary flex items-center gap-2">
                <Share2 className="w-4 h-4" /> Condividi
              </button>
              <button onClick={start} data-testid="quiz-retry-btn"
                className="px-5 py-2.5 rounded-xl font-semibold text-sm flex items-center gap-2"
                style={{ background: '#1A1A18', color: '#E8E8E4', border: '1px solid rgba(255,255,255,0.1)' }}>
                <RotateCcw className="w-4 h-4" /> Riprova
              </button>
            </div>
            {shareUrl && (
              <div className="mt-4">
                <label className="text-xs text-[#888780] block mb-1.5">Copia questo link:</label>
                <input type="text" readOnly value={SHARE_TEXT} onFocus={e => e.target.select()}
                  data-testid="quiz-share-fallback"
                  className="w-full px-3 py-2 rounded-lg text-xs text-[#E8E8E4]"
                  style={{ background: '#1A1A18', border: '1px solid rgba(255,255,255,0.1)' }} />
              </div>
            )}
          </div>
        ) : score === 4 ? (
          <>
            <h3 className="text-xl font-bold text-[#E8E8E4] mb-2">Ottimo risultato!</h3>
            <p className="text-sm text-[#888780] mb-5" data-testid="quiz-result-4">Rileggi la guida per completare la tua preparazione.</p>
            <button onClick={start} data-testid="quiz-retry-btn"
              className="px-5 py-2.5 rounded-xl font-semibold text-sm sapiens-btn-primary inline-flex items-center gap-2">
              <RotateCcw className="w-4 h-4" /> Riprova
            </button>
          </>
        ) : (
          <>
            <h3 className="text-xl font-bold text-[#E8E8E4] mb-2">Continua a studiare</h3>
            <p className="text-sm text-[#888780] mb-5" data-testid="quiz-result-low">Continua a studiare — sei sulla strada giusta!</p>
            <button onClick={start} data-testid="quiz-retry-btn"
              className="px-5 py-2.5 rounded-xl font-semibold text-sm sapiens-btn-primary inline-flex items-center gap-2">
              <RotateCcw className="w-4 h-4" /> Riprova
            </button>
          </>
        )}
      </motion.div>

      {toast && (
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
          className="fixed bottom-8 left-1/2 -translate-x-1/2 px-4 py-2.5 rounded-xl text-sm font-medium z-50"
          style={{ background: '#3D7A52', color: '#fff' }}
          data-testid="quiz-toast">
          {toast}
        </motion.div>
      )}
    </div>
  );
}
